"use client";
import styles from "@styles/page-hero.module.css";
import { usePathname } from "next/navigation";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Nav } from "../nav";
import { path_and_hero_text } from "./data";
import downloadHero from "@images/download_hero.webp";

const NO_IMAGE_OR_TEXT_PATHS: string[] = [
  "/",
  "/website-policy",
  "/refund-policy",
  "/study-guide",
  "/plagiarism-policy",
  "/articles",
  "/contact-us",
  "/auth/login",
  "/auth/register",
  "/auth/activation-link",
  "/conflict-resolution",
  "/endowment",
  "/password-reset",
  "/career",
  "/events",
  "/examination-grading-system",
  "/admission",
  "/foreign-students",
  "/book-appointment",
  "/privacy-policy",
  "/account",
  "/accessibility",
  "/admin/chat",
  "/admin/login",
  "/tuition-fees",
];

const EXCLUDE_NAV_AND_HERO: string[] = [
  "/landing/natural-medicine-pathway",
  "/dashboard/admission-letter",
];

const EXCLUDE_HERO_SECTION: string[] = [
  "/dashboard",
  "/test",
  "/scholarship",
  "/programmes",
  "/landing/certificate-courses",
];

export default function PageHero() {
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState<boolean>(false);

  // Whether this route shows the hero banner/text — derived synchronously from
  // the pathname (identical on server and client, so SSR-safe). Computing this
  // during render reserves the 55vh banner's space from the first paint.
  // Previously it was flipped on in a post-mount effect, which injected the
  // banner late and shoved all page content down (large CLS).
  const hasImageOrText = (() => {
    const dynamicExcluded =
      /^\/articles\/.+$/.test(pathname) ||
      /^\/programmes\/.+$/.test(pathname) ||
      /^\/verification\/.+$/.test(pathname) ||
      /^\/faculty-members\/.+$/.test(pathname) ||
      /^\/events\/.+$/.test(pathname) ||
      /^\/password-reset\/.+$/.test(pathname);
    return !(NO_IMAGE_OR_TEXT_PATHS.includes(pathname) || dynamicExcluded);
  })();

  const heroText = path_and_hero_text[pathname];
  const shortText = heroText?.shortText;
  const longText = heroText?.longText;

  // Scroll to top on route change (but not when navigating to an in-page hash).
  useEffect(() => {
    if (!window.location.href.includes("#")) {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    }
  }, [pathname]);

  // Fade the hero text in shortly after load (opacity/transform only — no CLS
  // since the container's space is already reserved at render).
  useEffect(() => {
    if (!hasImageOrText) {
      setIsVisible(false);
      return;
    }
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [hasImageOrText]);

  if (EXCLUDE_NAV_AND_HERO.includes(pathname)) {
    return null;
  }
  if (EXCLUDE_HERO_SECTION.some((path) => pathname.startsWith(path))) {
    return (
      <section>
        <Nav lightBg />
      </section>
    );
  }
  return (
    <React.Fragment>
      {pathname !== "/dashboard" && pathname !== "/404" && (
        <section id="pageHeroContainer" className={styles.page_hero_container}>
          <Nav bg={pathname !== "/"} lightBg={pathname !== "/"} />
          {hasImageOrText && (
            <div className={styles.page_hero_banner}>
              <Image
                src={downloadHero}
                alt=""
                fill
                priority
                sizes="100vw"
                className={styles.page_hero_banner_img}
              />
            </div>
          )}
          {hasImageOrText && (
            <div
              className={`${styles.page_hero_main} ${
                isVisible ? styles.show : ""
              }`}
            >
              <span className="text-bold text-oicnm-green">{shortText}</span>
              <h1>{longText}</h1>
            </div>
          )}
        </section>
      )}
    </React.Fragment>
  );
}
